import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Check, Clock, X, AlertCircle, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import type { WhatsAppTemplate } from '@/api/tenants/tenants.types';
import { WhatsAppTemplatePreview } from './WhatsAppTemplatePreview';

interface TemplateDetailsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    template: WhatsAppTemplate | null;
    isLoading: boolean;
}

export const TemplateDetailsDialog = ({ 
    isOpen, 
    onClose, 
    template, 
    isLoading 
}: TemplateDetailsDialogProps) => {
    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return (
                    <Badge variant="default" className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700">
                        <Check className="w-3 h-3 mr-1" />
                        Approved
                    </Badge>
                );
            case 'pending':
            case 'submitted':
                return (
                    <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                    </Badge>
                );
            case 'rejected':
                return (
                    <Badge variant="destructive" className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700">
                        <X className="w-3 h-3 mr-1" />
                        Rejected
                    </Badge>
                );
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };



    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl p-0 overflow-hidden shadow-xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950/40 dark:to-blue-900/40 border-b dark:border-blue-800/30 px-6 py-3">
                    <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-lg bg-blue-600 dark:bg-blue-600 flex items-center justify-center shadow-sm">
                            <FileText className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <DialogTitle className="text-lg font-semibold truncate">
                                {isLoading ? 'Loading Template...' : template?.friendly_name || template?.name || 'Template Details'}
                            </DialogTitle>
                            <DialogDescription className="text-sm mt-0.5">
                                WhatsApp template details and components
                            </DialogDescription>
                        </div>
                    </div>
                </div>
                
                {/* Content */}
                <div 
                    className="p-4 space-y-4 max-h-[75vh] overflow-y-auto"
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) hsl(var(--muted) / 0.3)'
                    }}
                >
                    {isLoading ? (
                        <div className="space-y-6">
                            {/* Loading Skeleton */}
                            <div className="space-y-3">
                                {/* First Row: Basic Info and Status */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {/* Basic Information Card Skeleton */}
                                    <Card className="bg-gradient-to-br from-green-50/50 to-green-100/50 dark:from-green-950/20 dark:to-green-900/20 border-green-200/50 dark:border-green-800/50 shadow-sm">
                                        <CardHeader className="pb-2 pt-3 px-3">
                                            <div className="flex items-center gap-2">
                                                <Skeleton className="h-4 w-4" />
                                                <Skeleton className="h-4 w-24" />
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-2 px-3 pb-3">
                                            <div className="flex justify-between text-xs">
                                                <Skeleton className="h-3 w-12" />
                                                <Skeleton className="h-3 w-20" />
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <Skeleton className="h-3 w-16" />
                                                <Skeleton className="h-3 w-24" />
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <Skeleton className="h-3 w-14" />
                                                <Skeleton className="h-3 w-8" />
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <Skeleton className="h-3 w-12" />
                                                <Skeleton className="h-3 w-16" />
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <Skeleton className="h-3 w-10" />
                                                <Skeleton className="h-3 w-20" />
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Status & IDs Card Skeleton */}
                                    <Card className="bg-gradient-to-br from-blue-50/50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/20 border-blue-200/50 dark:border-blue-800/50 shadow-sm">
                                        <CardHeader className="pb-2 pt-3 px-3">
                                            <div className="flex items-center gap-2">
                                                <Skeleton className="h-4 w-4" />
                                                <Skeleton className="h-4 w-20" />
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-2 px-3 pb-3">
                                            <div className="flex justify-between items-center text-xs">
                                                <Skeleton className="h-3 w-10" />
                                                <Skeleton className="h-5 w-16 rounded-full" />
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <Skeleton className="h-3 w-16" />
                                                <Skeleton className="h-4 w-32 rounded" />
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <Skeleton className="h-3 w-14" />
                                                <Skeleton className="h-4 w-28 rounded" />
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <Skeleton className="h-3 w-16" />
                                                <Skeleton className="h-4 w-8 rounded" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Second Row: Agent Assignment Skeleton */}
                                <Card className="bg-gradient-to-br from-purple-50/50 to-purple-100/50 dark:from-purple-950/20 dark:to-purple-900/20 border-purple-200/50 dark:border-purple-800/50 shadow-sm">
                                    <CardHeader className="pb-2 pt-3 px-3">
                                        <div className="flex items-center gap-2">
                                            <Skeleton className="h-4 w-4" />
                                            <Skeleton className="h-4 w-28" />
                                        </div>
                                    </CardHeader>
                                    <CardContent className="px-3 pb-3">
                                        <div className="flex flex-wrap gap-1">
                                            <Skeleton className="h-6 w-16 rounded-full" />
                                            <Skeleton className="h-6 w-16 rounded-full" />
                                            <Skeleton className="h-6 w-16 rounded-full" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Template Components Skeleton */}
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Skeleton className="h-4 w-4" />
                                    <Skeleton className="h-4 w-32" />
                                </div>
                                <div className="space-y-4">
                                    {/* Component Card 1 */}
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <Skeleton className="h-4 w-24" />
                                        </CardHeader>
                                        <CardContent className="pt-0">
                                            <div className="bg-muted/30 p-3 rounded-md">
                                                <Skeleton className="h-3 w-full mb-1" />
                                                <Skeleton className="h-3 w-3/4 mb-1" />
                                                <Skeleton className="h-3 w-1/2" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    {/* Component Card 2 */}
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <Skeleton className="h-4 w-20" />
                                        </CardHeader>
                                        <CardContent className="pt-0">
                                            <div className="bg-muted/30 p-3 rounded-md">
                                                <Skeleton className="h-3 w-full mb-1" />
                                                <Skeleton className="h-3 w-2/3" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    ) : template ? (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                        {/* Template Info Cards */}
                        <div className="space-y-3">
                            {/* First Row: Basic Info and Status */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                {/* Basic Information Card */}
                                <Card className="bg-gradient-to-br from-green-50/50 to-green-100/50 dark:from-green-950/20 dark:to-green-900/20 border-green-200/50 dark:border-green-800/50 shadow-sm">
                                    <CardHeader className="pb-2 pt-3 px-3">
                                        <div className="flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-green-600 dark:text-green-400" />
                                            <CardTitle className="text-sm font-medium">Basic Information</CardTitle>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-2 px-3 pb-3">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Name:</span>
                                            <span className="font-medium">{template.name}</span>
                                        </div>
                                        {template.friendly_name && (
                                            <div className="flex justify-between text-xs">
                                                <span className="text-muted-foreground">Friendly Name:</span>
                                                <span className="font-medium">{template.friendly_name}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Language:</span>
                                            <span className="font-medium">{template.language ? template.language.toUpperCase() : '-'}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Category:</span>
                                            <span className="font-medium">{template.category}</span>
                                        </div>
                                        {template.group && (
                                            <div className="flex justify-between text-xs">
                                                <span className="text-muted-foreground">Group:</span>
                                                <span className="font-medium">{template.group}</span>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Status & IDs Card */}
                                <Card className="bg-gradient-to-br from-blue-50/50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/20 border-blue-200/50 dark:border-blue-800/50 shadow-sm">
                                    <CardHeader className="pb-2 pt-3 px-3">
                                        <div className="flex items-center gap-2">
                                            <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                            <CardTitle className="text-sm font-medium">Status & IDs</CardTitle>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-2 px-3 pb-3">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground">Status:</span>
                                            {getStatusBadge(template.status)}
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Template ID:</span>
                                            <span className="font-mono text-xs bg-muted/50 px-1 py-0.5 rounded">{template.template_id}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Namespace:</span>
                                            <span className="font-mono text-xs bg-muted/50 px-1 py-0.5 rounded">{template.namespace}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Internal ID:</span>
                                            <span className="font-mono text-xs bg-muted/50 px-1 py-0.5 rounded">{template.id}</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Second Row: Agent Assignment */}
                            <Card className="bg-gradient-to-br from-purple-50/50 to-purple-100/50 dark:from-purple-950/20 dark:to-purple-900/20 border-purple-200/50 dark:border-purple-800/50 shadow-sm">
                                <CardHeader className="pb-2 pt-3 px-3">
                                    <div className="flex items-center gap-2">
                                        <AlertCircle className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                        <CardTitle className="text-sm font-medium">Available to Agents</CardTitle>
                                    </div>
                                </CardHeader>
                                <CardContent className="px-3 pb-3">
                                    {template.agent_ids && template.agent_ids.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {template.agent_ids.map((agentId) => (
                                                <Badge key={agentId} variant="outline" className="bg-background text-xs px-2 py-0.5">
                                                    Agent {agentId}
                                                </Badge>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-2 text-muted-foreground">
                                            <p className="text-xs">Available to all agents</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Template Preview */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
                                <h4 className="font-medium text-sm">WhatsApp Preview</h4>
                            </div>
                            <WhatsAppTemplatePreview template={template} />
                        </div>
                        </motion.div>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                            <p>Template not found</p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
